<template>
  <div class="subscription-usage">
    <Sidebar />
    
    <div class="main-content">
      <header class="page-header">
        <h1 class="page-title">Subscription & Usage</h1>
      </header>

      <div class="page-container">
        <div class="tab-navigation">
          <button 
            @click="activeTab = 'subscriptions'" 
            :class="['tab-btn', { active: activeTab === 'subscriptions' }]"
          >
            Subscriptions
          </button>
          <button 
            @click="activeTab = 'usage'" 
            :class="['tab-btn', { active: activeTab === 'usage' }]"
            :disabled="!selectedProduct"
          >
            Usage Details
            <span v-if="selectedProduct" class="product-name">- {{ selectedProduct.name }}</span>
          </button>
        </div>

        <div class="tab-content">
          <!-- Subscriptions Tab -->
          <div v-if="activeTab === 'subscriptions'" class="subscriptions-tab">
            <div class="tab-header">
              <div class="header-content">
                <h2>Your Subscriptions</h2>
                <p class="tab-description">Manage your subscribed products and services</p>
              </div>
              <button @click="refreshSubscriptions" class="refresh-btn" :disabled="loading" title="Refresh data">
                <i class="fas fa-sync-alt" :class="{ spinning: loading }"></i>
                Refresh
              </button>
            </div>
            
            <div v-if="loading" class="loading-state">
              <div class="loading-spinner"></div>
              <p>Loading subscriptions...</p>
            </div>
            
            <div v-else-if="error" class="error-state">
              <div class="error-icon">⚠️</div>
              <h3>Error</h3>
              <p>{{ error }}</p>
              <button @click="fetchSubscriptions" class="retry-btn">Retry</button>
            </div>
            
            <div v-else-if="subscriptions.length === 0" class="empty-state">
              <div class="empty-icon">📋</div>
              <h3>No Active Subscriptions</h3>
              <p>You don't have any active subscriptions yet.</p>
            </div>
            
            <div v-else class="subscriptions-grid">
              <div 
                v-for="subscription in subscriptions" 
                :key="subscription.id"
                class="subscription-card"
                :class="{ inactive: subscription.status === 'inactive' }"
                @click="subscription.status === 'active' ? selectProduct(subscription) : null"
              >
                <div class="subscription-header">
                  <div class="product-info">
                    <h3 class="product-name">{{ subscription.name }}</h3>
                    <span class="product-type">{{ subscription.type }}</span>
                  </div>
                  <div class="subscription-status" :class="subscription.status">
                    {{ subscription.status }}
                  </div>
                </div>
                
                <div class="subscription-details">
                  <div class="detail-item">
                    <span class="label">Plan:</span>
                    <span class="value">{{ subscription.plan }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="label">Subscribe Date:</span>
                    <span class="value">{{ subscription.subscribeDate }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="label">Endpoint:</span>
                    <div class="endpoint-value">
                      <span class="value">{{ subscription.endpoint }}</span>
                      <button class="copy-btn" @click.stop="copyEndpoint(subscription.endpoint)" title="Copy endpoint">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" stroke-width="2" fill="none"/>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" stroke-width="2" fill="none"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
                
                <div class="subscription-actions">
                  <button 
                    class="view-usage-btn" 
                    :disabled="subscription.status !== 'active'"
                    @click.stop="subscription.status === 'active' ? selectProduct(subscription) : null"
                  >
                    View Usage
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Usage Details Tab -->
          <div v-if="activeTab === 'usage'" class="usage-tab">
            <div class="tab-header">
              <div class="header-content">
                <h2>Usage Details</h2>
                <p class="tab-description" v-if="selectedProduct">
                  Usage statistics for {{ selectedProduct.name }}
                </p>
              </div>
              <div class="header-actions">
                <div class="date-filter">
                  <div class="date-inputs">
                    <input 
                      type="date" 
                      v-model="dateRange.start" 
                      class="date-input"
                      :max="getCurrentDate()"
                      required
                    >
                    <span class="date-separator">to</span>
                    <input 
                      type="date" 
                      v-model="dateRange.end" 
                      class="date-input"
                      :min="dateRange.start"
                      :max="getCurrentDate()"
                      required
                    >
                  </div>
                  <button 
                    @click="applyDateFilter" 
                    class="filter-btn"
                    :disabled="usageLoading"
                  >
                    <i class="fas fa-search"></i>
                    Query
                  </button>
                </div>
              </div>
            </div>
            
            <div v-if="!selectedProduct" class="empty-state">
              <div class="empty-icon">📊</div>
              <h3>No Product Selected</h3>
              <p>Please select a product from the Subscriptions tab to view usage details.</p>
            </div>
            
            <div v-else class="usage-content">
              <div v-if="usageLoading" class="loading-state">
                <div class="loading-spinner"></div>
                <p>Loading usage data...</p>
              </div>
              
              <!-- Usage Summary -->
              <div v-else class="usage-summary">
                <div class="summary-card">
                  <div class="summary-icon">📊</div>
                  <div class="summary-info">
                    <h3>{{ usageData.totalUsed.toLocaleString() }}</h3>
                    <p>Total Usage</p>
                  </div>
                </div>
                
                
                <div class="summary-card">
                  <div class="summary-icon">📅</div>
                  <div class="summary-info">
                    <h3>{{ usageData.totalDays }}</h3>
                    <p>Days Queried</p>
                  </div>
                </div>
                
                <div class="summary-card">
                  <div class="summary-icon">🔥</div>
                  <div class="summary-info">
                    <h3>{{ usageData.peakDaily.toLocaleString() }}</h3>
                    <p>Peak Daily Usage</p>
                  </div>
                </div>
              </div>
              
              <!-- Usage Period Info -->
              <div class="usage-period">
                <div class="period-header">
                  <h3>Query Period</h3>
                  <span class="period-text">
                    {{ formatDateRange(dateRange.start, dateRange.end) }}
                  </span>
                </div>
                <div class="period-stats">
                  <span class="stat-item">
                    <strong>{{ usageData.totalUsed.toLocaleString() }}</strong> total usage
                  </span>
                  <span class="stat-divider">•</span>
                  <span class="stat-item">
                    <strong>{{ usageData.totalDays }}</strong> days
                  </span>
                </div>
              </div>
              
              <!-- Usage History -->
              <div class="usage-history">
                <h3>Usage History</h3>
                <div class="history-table">
                  <div class="table-header">
                    <div class="table-cell">Date</div>
                    <div class="table-cell">Usage</div>
                    <div class="table-cell">Unit</div>
                  </div>
                  <div 
                    v-for="record in usageData.history" 
                    :key="record.period"
                    class="table-row"
                  >
                    <div class="table-cell">{{ record.period }}</div>
                    <div class="table-cell">{{ record.used.toLocaleString() }}</div>
                    <div class="table-cell">{{ record.unit }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue'
import Sidebar from '@/components/Sidebar.vue'
import axios from 'axios'
import { handleTokenExpired, isTokenExpiredError } from '@/utils/auth.js'

axios.defaults.withCredentials = true

const setupAxiosInterceptors = () => {
  axios.interceptors.request.use(
    (config) => {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
      if (userInfo.token) {
        // 只设置cookie到document，让浏览器自动处理
        document.cookie = `console_cs_token=${userInfo.token}; path=/; samesite=lax`
        console.log('设置cookie:', `console_cs_token=${userInfo.token}`)
      }
      // 新增：打印最终请求URL
      const fullUrl = (config.baseURL ? config.baseURL.replace(/\/$/, '') : window.location.origin) + config.url;
      console.log('最终请求URL:', fullUrl);
      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )
}

export default {
  name: 'SubscriptionUsage',
  components: {
    Sidebar
  },
  setup() {
    const activeTab = ref('subscriptions')
    const loading = ref(false)
    const usageLoading = ref(false)
    const selectedProduct = ref(null)
    const error = ref('')
    
    // Helper function to get current date
    const getCurrentDate = () => {
      const today = new Date()
      return today.toISOString().split('T')[0]
    }
    
    // Helper function to get date N days ago
    const getDateDaysAgo = (days) => {
      const date = new Date()
      date.setDate(date.getDate() - days)
      return date.toISOString().split('T')[0]
    }
    
    // Get user-specific cache key to prevent data leakage between different users
    const getUserCacheKey = (key) => {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
      const userIdentifier = userInfo.customer_code || userInfo.email || 'anonymous'
      return `${key}_${userIdentifier}`
    }
    
    // Cached subscription data - user-specific to prevent cross-user data leakage
    const subscriptions = ref(JSON.parse(sessionStorage.getItem(getUserCacheKey('subscriptions')) || '[]'))
    
    // Date range for usage query - default to last 7 days
    const dateRange = reactive({
      start: getDateDaysAgo(7),
      end: getCurrentDate()
    })
    
    // Updated usage data structure without limits
    const usageData = reactive({
      totalUsed: 0,
      averageDaily: 0,
      totalDays: 0,
      peakDaily: 0,
      history: []
    })
    
    // Get user info for API calls
    const userInfo = computed(() => {
      const storedUserInfo = localStorage.getItem('userInfo')
      return storedUserInfo ? JSON.parse(storedUserInfo) : null
    })
    
    // Fetch subscription list
    const fetchSubscriptions = async () => {
      loading.value = true
      error.value = ''
      
      try {
        console.log('发送订阅列表请求到:', '/platform-proxy/v1/ai/marketPlace/search/self/service/subscription/list')
        const response = await axios.get('/platform-proxy/v1/ai/marketPlace/search/self/service/subscription/list', {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        })
        
        console.log('订阅列表响应状态:', response.status)
        console.log('订阅列表响应数据:', response.data)
        console.log('订阅列表响应头:', response.headers)
        console.log('响应内容类型:', response.headers['content-type'])
        
        // Check if response is HTML instead of JSON
        if (typeof response.data === 'string' && response.data.includes('<!doctype html>')) {
          console.error('API返回了HTML页面而不是JSON数据，可能是认证失败或API不存在')
          error.value = 'API authentication failed or endpoint not found. Please check your login status.'
          return
        }
        
        if (response.data && response.data.code === '0') {
          // Transform API data to match our UI structure
          subscriptions.value = response.data.data.map(item => ({
            id: item.endpoint,
            name: getProductName(item.plan),
            type: 'API Service',
            status: item.enable ? 'active' : 'inactive', // Use 'enable' field instead of 'status'
            plan: item.plan,
            endpoint: item.endpoint,
            subscribeDate: item.subscribeDate,
            nextBilling: calculateNextBilling(item.subscribeDate)
          }))
          
          // Cache subscriptions data with user-specific key
          sessionStorage.setItem(getUserCacheKey('subscriptions'), JSON.stringify(subscriptions.value))
        } else if (response.data && response.data.message) {
          error.value = response.data.message
        } else {
          error.value = 'Invalid response format from server'
        }
      } catch (err) {
        console.error('Fetch subscriptions error:', err)
        
        // 首先检查是否是token过期错误
        if (err.response) {
          const { status, data } = err.response
          console.error('Error response status:', status)
          console.error('Error response data:', data)
          
          // 检查token过期
          if (status === 401 || status === 403 || 
              (status === 500 && isTokenExpiredError({ status, message: data?.message }, data))) {
            console.warn('Token expired, redirecting to login...')
            handleTokenExpired()
            return // 不设置错误消息，因为会跳转到登录页
          }
          
          // 其他HTTP错误
          if (status === 404) {
            error.value = 'API endpoint not found. Please check the server configuration.'
          } else {
            error.value = `Server error: ${status} - ${data?.message || 'Unknown error'}`
          }
        } else if (err.request) {
          console.error('No response received:', err.request)
          error.value = 'No response from server. Please check your connection and server status.'
        } else {
          error.value = 'Request failed: ' + err.message
        }
      } finally {
        loading.value = false
      }
    }
    
    // Fetch real-time usage data with date range
    const fetchUsageData = async (productCode, forceRefresh = false) => {
      if (!productCode) return
      
      usageLoading.value = true
      
      try {
        // Convert date strings to UTC timestamps
        const startDate = new Date(dateRange.start + 'T00:00:00Z')
        const endDate = new Date(dateRange.end + 'T23:59:59Z')
        const startTimestamp = Math.floor(startDate.getTime() / 1000)
        const endTimestamp = Math.floor(endDate.getTime() / 1000)
        
        const mappedProductCode = getUsageProductCode(productCode)
        
        console.log('发送使用量请求到:', '/measure-proxy/v1/measure/marketPlace/measure/queryInstantByDate')
        console.log('请求参数:', {
          productCode: mappedProductCode,
          start: startTimestamp,
          end: endTimestamp
        })
        
        const response = await axios.get('/measure-proxy/v1/measure/marketPlace/measure/queryInstantByDate', {
          params: {
            productCode: mappedProductCode,
            start: startTimestamp,
            end: endTimestamp
          },
          withCredentials: true,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'application/json'
          }
        })
        
        console.log('使用量响应状态:', response.status)
        console.log('使用量响应数据:', response.data)
        
        if (response.data && response.data.code === '0') {
          const data = response.data.data || []
          console.log('解析的使用量数据:', data)
          
          // Calculate usage statistics
          let totalUsed = 0
          const historyData = []
          const dailyUsages = []
          
          data.forEach((item, index) => {
            try {
              // Handle the new data structure - data array might be empty
              let dailyUsage = 0
              
              if (item && item.data && Array.isArray(item.data) && item.data.length > 0) {
                // If data array has values, use them
                const value = item.data[0] && item.data[0].value && item.data[0].value[1]
                dailyUsage = parseInt(value) || 0
              } else {
                // If data array is empty, assume 0 usage for now
                dailyUsage = 0
              }
              
              totalUsed += dailyUsage
              dailyUsages.push(dailyUsage)
              
              // Parse date from usageBeginTime
              let dateStr = new Date().toLocaleDateString()
              if (item && item.usageBeginTime && typeof item.usageBeginTime === 'string') {
                const dateParts = item.usageBeginTime.split(' ')
                if (dateParts.length > 0) {
                  dateStr = dateParts[0]
                }
              }
              
              historyData.push({
                period: dateStr,
                used: dailyUsage,
                unit: (item && item.unit) || 'Times'
              })
            } catch (error) {
              console.error(`Error processing usage data item ${index}:`, error, item)
              // Continue processing other items even if one fails
            }
          })
          
          // Calculate statistics
          const totalDays = data.length
          const averageDaily = totalDays > 0 ? Math.round(totalUsed / totalDays) : 0
          const peakDaily = dailyUsages.length > 0 ? Math.max.apply(Math, dailyUsages) : 0
          
          // Update usage data with safe values
          usageData.totalUsed = totalUsed || 0
          usageData.averageDaily = averageDaily || 0
          usageData.totalDays = totalDays || 0
          usageData.peakDaily = peakDaily || 0
          usageData.history = historyData || []
          
          // Cache usage data with user-specific key
          const cacheKey = getUserCacheKey(`usage_${productCode}_${dateRange.start}_${dateRange.end}`)
          sessionStorage.setItem(cacheKey, JSON.stringify(usageData))
          
          console.log('更新后的使用量数据:', {
            totalUsed,
            averageDaily,
            totalDays,
            peakDaily,
            historyCount: historyData.length,
            historyData: historyData.slice(0, 3) // Show first 3 entries for debugging
          })
          
        } else {
          console.error('Usage API error:', response.data?.message || 'Unknown error')
          error.value = 'Failed to fetch usage data: ' + (response.data?.message || 'Unknown error')
        }
      } catch (err) {
        console.error('Fetch usage error:', err)
        
        // 首先检查是否是token过期错误
        if (err.response) {
          const { status, data } = err.response
          console.error('Error response status:', status)
          console.error('Error response data:', data)
          
          // 检查token过期
          if (status === 401 || status === 403 || 
              (status === 500 && isTokenExpiredError({ status, message: data?.message }, data))) {
            console.warn('Token expired during usage data fetch, redirecting to login...')
            handleTokenExpired()
            return // 不设置错误消息，因为会跳转到登录页
          }
          
          // 其他错误
          error.value = `Usage API error: ${status} - ${data?.message || 'Unknown error'}`
        } else {
          error.value = 'Failed to fetch usage data: ' + err.message
        }
      } finally {
        usageLoading.value = false
      }
    }
    
    // Helper functions
    const formatDateRange = (startDate, endDate) => {
      const start = new Date(startDate)
      const end = new Date(endDate)
      const options = { year: 'numeric', month: 'short', day: 'numeric' }
      
      if (startDate === endDate) {
        return start.toLocaleDateString('en-US', options)
      }
      
      return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`
    }
    
    const applyDateFilter = async () => {
      if (selectedProduct.value) {
        await fetchUsageData(selectedProduct.value.plan)
      }
    }
    const getProductName = (plan) => {
      const nameMap = {
        'smartSearch': 'Smart Search API',
        'basicSearch': 'Basic Search API',
        'premiumSearch': 'Premium Search API'
      }
      return nameMap[plan] || plan
    }
    
    // Use plan directly as product code
    const getUsageProductCode = (plan) => {
      return plan
    }
    
    
    const calculateNextBilling = (subscribeDate) => {
      const date = new Date(subscribeDate)
      date.setMonth(date.getMonth() + 1)
      return date.toISOString().split('T')[0]
    }
    
    const selectProduct = async (product) => {
      selectedProduct.value = product
      activeTab.value = 'usage'
      
      // Use plan as the identifier for usage data with user-specific cache
      const productIdentifier = product.plan
      const cacheKey = getUserCacheKey(`usage_${productIdentifier}_${dateRange.start}_${dateRange.end}`)
      const cachedUsage = sessionStorage.getItem(cacheKey)
      
      if (cachedUsage) {
        // Load from cache
        const cached = JSON.parse(cachedUsage)
        Object.assign(usageData, cached)
      } else {
        // Fetch real usage data for the selected product using plan
        await fetchUsageData(productIdentifier)
      }
    }
    
    // Add refresh functions
    const refreshSubscriptions = () => {
      fetchSubscriptions()
    }
    
    
    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }
    
    const copyEndpoint = async (endpoint) => {
      try {
        await navigator.clipboard.writeText(endpoint)
        alert('Endpoint copied to clipboard!')
      } catch (err) {
        console.error('Failed to copy endpoint:', err)
        alert('Failed to copy endpoint')
      }
    }
    
    onMounted(() => {
      setupAxiosInterceptors()
      
      // Always fetch subscriptions on mount to ensure we get the latest data with current user's token
      // This prevents showing cached data from previous user sessions
      fetchSubscriptions()
    })
    
    return {
      activeTab,
      loading,
      usageLoading,
      selectedProduct,
      subscriptions,
      usageData,
      error,
      dateRange,
      selectProduct,
      formatDate,
      copyEndpoint,
      fetchSubscriptions,
      refreshSubscriptions,
      getCurrentDate,
      formatDateRange,
      applyDateFilter
    }
  }
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

.subscription-usage {
  height: 100vh;
  display: flex;
  background: #f8fafc;
  color: #1f2937;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  overflow: hidden;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  transition: margin-left 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  height: 100vh;
  overflow: hidden;
}

.page-header {
  display: flex;
  align-items: center;
  padding: 24px 32px 24px 80px;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
  flex-shrink: 0;
}

.page-title {
  font-size: 32px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 600;
  margin: 0;
  color: #111827;
}

.page-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tab-navigation {
  display: flex;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  padding: 0 32px 0 80px;
}

.tab-btn {
  padding: 16px 24px;
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s ease;
  border-bottom: 2px solid transparent;
  position: relative;

  &.active {
    color: #4285f4;
    border-bottom-color: #4285f4;
  }

  &:hover:not(.active):not(:disabled) {
    color: #374151;
  }

  &:disabled {
    color: #d1d5db;
    cursor: not-allowed;
  }

  .product-name {
    font-size: 12px;
    opacity: 0.8;
  }
}

.tab-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.subscriptions-tab,
.usage-tab {
  flex: 1;
  padding: 32px;
  overflow-y: auto;
}

.tab-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  
  .header-content {
    h2 {
      font-size: 24px;
      font-weight: 600;
      margin: 0 0 8px 0;
      color: #111827;
    }

    .tab-description {
      color: #6b7280;
      font-size: 14px;
      margin: 0;
    }
  }
  
  .header-actions {
    display: flex;
    align-items: center;
    gap: 16px;
  }
}

.date-filter {
  display: flex;
  align-items: center;
  gap: 12px;
  
  .date-inputs {
    display: flex;
    align-items: center;
    gap: 8px;
    
    .date-input {
      padding: 8px 12px;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      font-size: 14px;
      color: #374151;
      background: #fff;
      min-width: 140px;
      cursor: pointer;
      
      &:hover {
        border-color: #9ca3af;
        background: #fafafa;
      }
      
      &:focus {
        outline: none;
        border-color: #4285f4;
        box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.1);
      }
      
      /* Ensure date picker is interactive */
      &::-webkit-calendar-picker-indicator {
        cursor: pointer;
        opacity: 0.7;
        transition: opacity 0.2s ease;
        
        &:hover {
          opacity: 1;
        }
      }
    }
    
    .date-separator {
      color: #6b7280;
      font-size: 14px;
      font-weight: 500;
    }
  }
}

.filter-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #4285f4;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #3367d6;
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  i {
    font-size: 14px;
  }
}

.refresh-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #374151;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #f3f4f6;
    border-color: #d1d5db;
    color: #111827;
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  i {
    font-size: 14px;
    transition: transform 0.3s ease;
    
    &.spinning {
      animation: spin 1s linear infinite;
    }
  }
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  text-align: center;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top: 3px solid #4285f4;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-icon,
.error-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.retry-btn {
  margin-top: 16px;
  padding: 8px 16px;
  background: #4285f4;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #3367d6;
    transform: translateY(-1px);
  }
}

.subscriptions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 24px;
  min-width: 0; /* Prevent grid overflow */
}

.subscription-card {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 0; /* Prevent card content overflow */
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    border-color: #d1d5db;
  }
  
  &.inactive {
    opacity: 0.7;
    background: #f9fafb;
    border-color: #f3f4f6;
    cursor: default;
    
    &:hover {
      opacity: 0.8;
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
    }
    
    .product-name {
      color: #6b7280 !important;
    }
  }
}

.subscription-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.product-info {
  .product-name {
    font-size: 18px;
    font-weight: 600;
    color: #111827;
    margin: 0 0 4px 0;
  }
  
  .product-type {
    font-size: 12px;
    color: #6b7280;
    background: #f3f4f6;
    padding: 2px 8px;
    border-radius: 12px;
  }
}

.subscription-status {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  
  &.active {
    background: #dcfce7;
    color: #166534;
  }
  
  &.inactive {
    background: #f3f4f6;
    color: #6b7280;
  }
  
  &.trial {
    background: #fef3c7;
    color: #92400e;
  }
  
  &.expired {
    background: #fee2e2;
    color: #991b1b;
  }
}

.subscription-details {
  margin-bottom: 20px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  
  .label {
    color: #6b7280;
    font-size: 14px;
  }
  
  .value {
    color: #111827;
    font-weight: 500;
    font-size: 14px;
  }
  
  .endpoint-value {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0; /* Allow flex item to shrink */
    
    .value {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1;
      min-width: 0;
    }
    
    .copy-btn {
      background: transparent;
      border: none;
      cursor: pointer;
      color: #6b7280;
      padding: 2px;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      flex-shrink: 0;
      
      &:hover {
        color: #4285f4;
        background: #f3f4f6;
        transform: scale(1.1);
      }
      
      &:active {
        transform: scale(0.95);
      }
      
      svg {
        flex-shrink: 0;
      }
    }
  }
}

.subscription-actions {
  display: flex;
  justify-content: flex-end;
}

.view-usage-btn {
  padding: 8px 16px;
  background: #4285f4;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #3367d6;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    opacity: 0.6;
    
    &:hover {
      background: #9ca3af;
      transform: none;
    }
  }
}

.usage-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.usage-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  min-width: 0; /* Prevent overflow */
}

.usage-period {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #e5e7eb;
  
  .period-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    
    h3 {
      font-size: 18px;
      font-weight: 600;
      margin: 0;
      color: #111827;
    }
    
    .period-text {
      font-size: 14px;
      color: #6b7280;
      font-weight: 500;
    }
  }
  
  .period-stats {
    display: flex;
    align-items: center;
    gap: 12px;
    
    .stat-item {
      font-size: 14px;
      color: #374151;
    }
    
    .stat-divider {
      color: #d1d5db;
      font-weight: bold;
    }
  }
}

.summary-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 16px;
  
  .summary-icon {
    font-size: 24px;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f3f4f6;
    border-radius: 12px;
  }
  
  .summary-info {
    h3 {
      font-size: 24px;
      font-weight: 700;
      margin: 0 0 4px 0;
      color: #111827;
    }
    
    p {
      font-size: 14px;
      color: #6b7280;
      margin: 0;
    }
  }
}

.usage-progress {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #e5e7eb;
  
  .progress-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    
    h3 {
      font-size: 18px;
      font-weight: 600;
      margin: 0;
      color: #111827;
    }
    
    .progress-text {
      font-size: 14px;
      color: #6b7280;
      font-weight: 500;
    }
  }
  
  .progress-bar {
    height: 8px;
    background: #f3f4f6;
    border-radius: 4px;
    overflow: hidden;
    
    .progress-fill {
      height: 100%;
      background: linear-gradient(135deg, #4285f4, #3367d6);
      transition: width 0.3s ease;
      
      &.warning {
        background: linear-gradient(135deg, #f59e0b, #d97706);
      }
    }
  }
}

.usage-history {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #e5e7eb;
  
  h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 20px 0;
    color: #111827;
  }
}

.history-table {
  .table-header {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr;
    gap: 16px;
    padding: 12px 0;
    border-bottom: 2px solid #e5e7eb;
    font-weight: 600;
    color: #374151;
    font-size: 14px;
  }
  
  .table-row {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr;
    gap: 16px;
    padding: 16px 0;
    border-bottom: 1px solid #f3f4f6;
    
    &:last-child {
      border-bottom: none;
    }
  }
  
  .table-cell {
    font-size: 14px;
    color: #374151;
    display: flex;
    align-items: center;
  }
}

/* Desktop responsive design for different screen resolutions */
@media (max-width: 1400px) {
  .usage-summary {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
  
  .subscriptions-grid {
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 20px;
  }
}

@media (max-width: 1200px) {
  .page-header {
    padding: 20px 24px 20px 60px;
  }
  
  .tab-navigation {
    padding: 0 24px 0 60px;
  }
  
  .subscriptions-tab,
  .usage-tab {
    padding: 24px;
  }
  
  .tab-header {
    .header-actions {
      flex-wrap: wrap;
      gap: 12px;
    }
  }
  
  .date-filter {
    .date-inputs {
      .date-input {
        min-width: 120px;
      }
    }
  }
}

@media (max-width: 1024px) {
  .usage-summary {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
  
  .subscriptions-grid {
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 16px;
  }
  
  .tab-header {
    flex-direction: column;
    align-items: stretch;
    gap: 20px;
    
    .header-actions {
      justify-content: flex-start;
    }
  }
}

@media (min-width: 1920px) {
  .subscriptions-grid {
    grid-template-columns: repeat(auto-fill, minmax(450px, 1fr));
    gap: 32px;
  }
  
  .usage-summary {
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
  }
  
  .page-header {
    padding: 32px 40px 32px 100px;
  }
  
  .tab-navigation {
    padding: 0 40px 0 100px;
  }
  
  .subscriptions-tab,
  .usage-tab {
    padding: 40px;
  }
}
</style>